import { httpsCallable } from 'firebase/functions'
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import { firebaseFunctions, firestoreDb } from './firebase'
import { callable } from './callable'
import {
  normalizeDemoFreeze,
  normalizeOwnerSettings,
  ownerSettingsToFirestore,
  type OwnerDemoFreeze,
  type OwnerSettings,
} from './ownerConsoleSettingsModel'
import type { DebugReporterRestriction, DebugTicketSeverity, DebugTicketStatus, DebugVisibleEvent } from './debugTicketService'
import { getOwnerTabSessionId } from '../utils/ownerSession'

export type OwnerTone = 'neutral' | 'good' | 'warn' | 'danger'
export type OwnerConfidence = 'Live' | 'Modeled' | 'Estimated' | 'Pending live feed'

export type OwnerMetric = {
  label: string
  value: string
  detail: string
  delta: string
  tone: OwnerTone
}

export type OwnerWorkspaceRow = {
  id: string
  name: string
  owner: string
  plan: string
  status: string
  users: number
  helpers: number
  channels?: number
  messages?: number
  fieldPasses: number
  storageGb: number
  storageLimitGb: number
  creditsSpent: number
  creditsAvailable: number
  aiCalls: number
  monthlyRevenue: number
  estimatedCost: number
  margin: number
  risk: 'Healthy' | 'Watch' | 'Critical'
  lastActivity: string
  modules: string[]
  alerts: string[]
}

export type OwnerDashboardSummary = {
  metrics: OwnerMetric[]
  totals: {
    workspaceCount: number
    userCount: number
    paidWorkspaceCount: number
    mrr: number
    openRiskCount: number
    failedAutomationCount: number
    analyticsEventCount: number
  }
  leaseMinutes?: number
}

export type OwnerWorkspaceList = {
  rows: OwnerWorkspaceRow[]
  limited: boolean
}

export type OwnerFeedbackReport = {
  id: string
  formId: string
  rating: number | null
  remark: string
  skipped: boolean
  status: string
  reporterId: string
  reporterEmail: string
  rewardCredits: number
  submittedAt: string
}

export type OwnerDebugReport = {
  id: string
  sessionId: string
  status: DebugTicketStatus
  severity: DebugTicketSeverity
  reporterId: string
  reporterEmail: string
  reporterName: string
  title: string
  description: string
  additionalMessage: string
  latestUserNote: string
  ownerNote: string
  resolutionNote: string
  eventCount: number
  errorCount: number
  warningCount: number
  networkFailureCount: number
  screenshotCount: number
  attachmentCount: number
  initialUrl: string
  viewport: string
  userAgent: string
  emailDelivery: string
  emailDeliveryOk: boolean
  emailDeliveryReason: string
  hasDiagnostics: boolean
  bundlePath: string
  visibleEvents: DebugVisibleEvent[]
  reopenCount: number
  createdAt: string
  updatedAt: string
  acceptedAt: string
  resolvedAt: string
  reopenedAt: string
  reporterRestriction?: DebugReporterRestriction | null
}

export type OwnerSectionUsageRow = {
  sectionKey: string
  label: string
  opens: number
  users: number
  workspaces: number
  lastSeen: string
}

export type OwnerInboxList<T> = {
  rows: T[]
  limited: boolean
}

export type OwnerRevenueRow = {
  key: string
  label: string
  value: string
  valueRupees: number
  detail: string
  source: string
  confidence: OwnerConfidence
}

export type OwnerRevenueBreakdown = {
  rows: OwnerRevenueRow[]
  totalRupees: number
  generatedAt: string
}

export type OwnerCostRow = {
  key: string
  label: string
  estimate: string
  estimateRupees: number
  source: string
  confidence: OwnerConfidence
  tone: OwnerTone
}

export type OwnerCostBreakdown = {
  rows: OwnerCostRow[]
  totalRupees: number
  generatedAt: string
}

export type OwnerAdoptionRow = {
  key: string
  label: string
  usage: number
  detail: string
  workspaceCount: number
}

export type OwnerAdoptionRollup = {
  rows: OwnerAdoptionRow[]
  totalWorkspaces: number
  generatedAt: string
}

export type OwnerOpsRow = {
  key: string
  subsystem: string
  state: 'Healthy' | 'Watch' | 'Critical'
  detail: string
  tone: OwnerTone
  count: number
}

export type OwnerOpsStatus = {
  rows: OwnerOpsRow[]
  generatedAt: string
}

export type OwnerActionKind =
  | 'GRANT_CREDITS'
  | 'GRANT_WORKSPACE_CREDITS'
  | 'GRANT_PERSONAL_CREDITS'
  | 'CHANGE_PLAN'
  | 'SUSPEND_WORKSPACE'
  | 'FORCE_USAGE_REFRESH'
  | 'RETRY_FAILED_JOB'
  | 'SET_MAINTENANCE_MODE'
  | 'RESET_WORKSPACE_CONTENT'
  | 'RESET_GAMIFICATION_FOR_USER'
  | 'RESET_CHAT_FOR_WORKSPACE'
  | 'RESET_AI_HISTORY_FOR_USER'
  | 'RESET_DESIGN_HUB_STALE'
  | 'FORCE_ONBOARDING_FOR_USER'
  | 'INSPECT_WORKSPACE'
  | 'CONTENT_BREAK_GLASS'
  | 'FORCE_SIGN_OUT_EVERYWHERE'
  | 'FORCE_PASSWORD_RESET'
  | 'TOGGLE_ACCOUNT_DISABLED'
  | 'SET_WORKSPACE_DEMO_UNLOCK'
  | 'SAVE_DEMO_STORY'
  | 'RESTORE_DEMO_STORY'
  | 'DELETE_DEMO_STORY'
  | 'SET_DEMO_FREEZE'
  | 'SET_PLAN_LIMITS'

export type OwnerActionInput = {
  kind: OwnerActionKind
  reason: string
  workspaceId?: string
  userId?: string
  plan?: string
  creditAmount?: number
  runId?: string
  automationId?: string
  workflowId?: string
  enabled?: boolean
  dryRun?: boolean
  message?: string
  confirmPhrase?: string
  previewToken?: string
  planLimits?: unknown
  /** R35 DL2: demo story id (restore, delete) and name (save). */
  storyId?: string
  storyName?: string
  /** R35 DL5: SET_DEMO_FREEZE. `active` starts or ends, `hours` (1 to 24, start only), `note`. */
  active?: boolean
  hours?: number
  note?: string
}

export type OwnerActionResult = {
  ok: true
  actionId: string
  kind: OwnerActionKind
  signature: string
  data?: Record<string, unknown>
}

export type OwnerActionLogEntry = {
  id: string
  kind: string
  status: 'SUCCESS' | 'FAILED' | 'PREVIEW' | 'UNKNOWN'
  actorId: string
  actorEmail: string
  actorRole?: string | null
  reason: string
  targetLabel: string
  workspaceId: string | null
  params: Record<string, unknown>
  signature: string
  errorMessage: string | null
  environment?: 'sit' | 'dev' | 'prod' | 'unknown'
  reversibleUntil?: number | null
  revertedAt?: number | null
  revertedBy?: string | null
  ipHash?: string | null
  userAgentHash?: string | null
  before?: Record<string, unknown> | null
  after?: Record<string, unknown> | null
  createdAt: string
}

export type OwnerActionLogList = {
  rows: OwnerActionLogEntry[]
  limited: boolean
}

export type OwnerActivityOutcome = 'SUCCESS' | 'FAILED' | 'PREVIEW' | 'UNKNOWN'
export type OwnerActivityEnvironment = 'sit' | 'dev' | 'prod' | 'unknown'
export type OwnerActivityScope = 'global' | 'workspace' | 'user' | 'breakglass' | 'unknown'

export type OwnerActivityFilters = {
  from?: string
  to?: string
  actor?: 'all' | 'self' | string
  kinds?: string[]
  outcomes?: OwnerActivityOutcome[]
  scope?: 'all' | OwnerActivityScope
  environments?: OwnerActivityEnvironment[]
}

export type OwnerActivityListInput = OwnerActivityFilters & {
  cursor?: string
  pageSize?: number
}

export type OwnerActivityExportInput = OwnerActivityFilters

export type OwnerActivityExportResult = {
  csv: string
  rowCount: number
  truncated: boolean
  exportActionId: string
}

export type OwnerActivityEntry = {
  id: string
  actionId: string
  storedActionId: string | null
  kind: string
  verb: string
  outcome: OwnerActivityOutcome
  actor: { id: string; label: string; role: string | null }
  subject: { label: string; scope: OwnerActivityScope; workspaceId: string | null; userId: string | null }
  reason: string
  errorMessage: string | null
  environment: OwnerActivityEnvironment
  createdAt: { iso: string; seconds: number; nanoseconds: number } | null
  before: Record<string, string | number | boolean | null> | null
  after: Record<string, string | number | boolean | null> | null
  metadata: {
    previewId: string | null
    reversibleUntil: number | null
    revertedAt: number | null
    revertedBy: string | null
    revertOf: string | null
    targetWorkspaceId: string | null
    signaturePresent: boolean
    integrity: 'unverified'
  }
}

export type OwnerActivityDetailResult = {
  row: OwnerActivityEntry
  revert: {
    revertible: boolean
    state: 'DONE' | 'IN_PROGRESS' | null
    revertActionId: string | null
  }
}

export type OwnerRevertDryRunResult = {
  kind: string
  target: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  blockedReason?: string
}

export type OwnerRevertSuccessResult = {
  ok: true
  actionId: string
  revertOf: string
  kind: 'REVERT'
  target: string
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  signature: string
}

export type OwnerRevertResult = OwnerRevertDryRunResult | OwnerRevertSuccessResult

export type OwnerActivityListResult = {
  rows: OwnerActivityEntry[]
  nextCursor: string | null
  scanComplete: boolean
  scannedRows: number
  pageSize: number
}

export type OwnerDebugReportInput = {
  sessionId: string
  title: string
  description: string
  severity: DebugTicketSeverity
  reporterEmail?: string | null
  reporterName?: string | null
  additionalMessage?: string
  eventCount: number
  errorCount: number
  warningCount: number
  networkFailureCount: number
  screenshotCount: number
  attachmentCount: number
  initialUrl: string
  viewport: string
  userAgent: string
  emailDelivery: string
  emailDeliveryOk: boolean
  emailDeliveryReason?: string
  hasDiagnostics: boolean
  bundlePath?: string
  visibleEvents?: DebugVisibleEvent[]
}

export type OwnerDebugBundleUrlResult = {
  ok: true
  url: string
  bundlePath: string
  expiresAt: string
}

export type GetOwnerHomeSnapshotInput = {
  timeZone: string
}

export type SectionState = 'ok' | 'unavailable'
export type QualityTier = 'live' | 'modeled'

export type OwnerHomeSectionTickets = {
  state: SectionState
  quality: 'live'
  count?: number
  breachedCount?: number
  hasBreach?: boolean
  truncated?: boolean
  message?: string
}

export type OwnerHomeSectionSignups = {
  state: SectionState
  quality: 'live'
  todayCount?: number
  sevenDayAverage?: number
  sevenDayTotal?: number
  dailyCounts?: number[]
  deltaVsAverage?: number
  message?: string
}

export type OwnerHomeSectionBurn = {
  state: SectionState
  quality: 'live'
  burnToday?: number
  burnMonth?: number
  message?: string
}

export type OwnerHomeSectionMoney = {
  state: SectionState
  quality: 'modeled'
  mrr?: number
  paidWorkspaceCount?: number
  burnMonth?: number
  burnMonthQuality?: 'live'
  message?: string
}

export type OwnerHomeSectionRisk = {
  state: SectionState
  quality: 'live'
  openRiskCount?: number
  failedAutomationCount?: number
  totalRiskCount?: number
  message?: string
}

export type OwnerHomeSectionTotals = {
  state: SectionState
  quality: 'live'
  workspaceCount?: number
  userCount?: number
  message?: string
}

export type OwnerHomeSectionRecentActions = {
  state: SectionState
  quality: 'live'
  actions?: OwnerActivityEntry[]
  message?: string
}

export type RecentTicketItem = {
  id: string
  title: string
  status: string
  priority: string
  updatedAt: string | null
}

export type OwnerHomeSectionRecentTickets = {
  state: SectionState
  quality: 'live'
  tickets?: RecentTicketItem[]
  message?: string
}

export type OwnerHomeSnapshot = {
  asOf: string
  leaseMinutes: number
  bounds: {
    dayStart: string
    weekStart: string
    monthStart: string
  }
  tickets?: OwnerHomeSectionTickets
  signups?: OwnerHomeSectionSignups
  burn?: OwnerHomeSectionBurn
  money?: OwnerHomeSectionMoney
  risk?: OwnerHomeSectionRisk
  totals?: OwnerHomeSectionTotals
  recentActions?: OwnerHomeSectionRecentActions
  recentTickets?: OwnerHomeSectionRecentTickets
}

const legacyCallable = <Input, Output>(name: string) => httpsCallable<Input, Output>(firebaseFunctions, name)
const ownerSettingsRef = () => doc(firestoreDb, 'system', 'config')

export const getOwnerDashboardSummary = async () =>
  (await legacyCallable<Record<string, never>, OwnerDashboardSummary>('getOwnerDashboardSummary')({})).data

export const getOwnerHomeSnapshot = async (
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): Promise<OwnerHomeSnapshot> =>
  callable<GetOwnerHomeSnapshotInput, OwnerHomeSnapshot>('getOwnerHomeSnapshot', { timeZone })

export const listOwnerWorkspaces = async () =>
  (await legacyCallable<Record<string, never>, OwnerWorkspaceList>('listOwnerWorkspaces')({})).data

export const listOwnerFeedbackReports = async () =>
  (await legacyCallable<Record<string, never>, OwnerInboxList<OwnerFeedbackReport>>('listOwnerFeedbackReports')({})).data

/**
 * @deprecated Use listSupportTickets from src/services/supportTicketsService.ts instead.
 */
export const listOwnerDebugReports = async () =>
  (await legacyCallable<Record<string, never>, OwnerInboxList<OwnerDebugReport>>('listOwnerDebugReports')({})).data

export const listOwnerSectionUsage = async () =>
  (await legacyCallable<Record<string, never>, OwnerInboxList<OwnerSectionUsageRow>>('listOwnerSectionUsage')({})).data

export const listOwnerRevenueBreakdown = async () =>
  (await legacyCallable<Record<string, never>, OwnerRevenueBreakdown>('listOwnerRevenueBreakdown')({})).data

export const listOwnerCostBreakdown = async () =>
  (await legacyCallable<Record<string, never>, OwnerCostBreakdown>('listOwnerCostBreakdown')({})).data

export const getOwnerAdoptionRollup = async () =>
  (await legacyCallable<Record<string, never>, OwnerAdoptionRollup>('getOwnerAdoptionRollup')({})).data

export const getOwnerOpsStatus = async () =>
  (await legacyCallable<Record<string, never>, OwnerOpsStatus>('getOwnerOpsStatus')({})).data

export const getOwnerSettings = async () => {
  const snapshot = await getDoc(ownerSettingsRef())
  return normalizeOwnerSettings(snapshot.exists() ? snapshot.data() : {})
}

export const subscribeOwnerSettings = (
  onChange: (settings: OwnerSettings) => void,
  onError?: (error: unknown) => void,
) => onSnapshot(ownerSettingsRef(), (snapshot) => {
  onChange(normalizeOwnerSettings(snapshot.exists() ? snapshot.data() : {}))
}, (error) => {
  onError?.(error)
})

/** R35 DL5.b: live `system/config.demoFreeze` (same document as the owner settings; the settings model drops the field). */
export const subscribeDemoFreeze = (
  onChange: (freeze: OwnerDemoFreeze) => void,
  onError?: (error: unknown) => void,
) => onSnapshot(ownerSettingsRef(), (snapshot) => {
  onChange(normalizeDemoFreeze(snapshot.exists() ? snapshot.data() : {}, Date.now()))
}, (error) => {
  onError?.(error)
})

export const saveOwnerSettings = async (settings: OwnerSettings, actorUid: string) => {
  const patch = ownerSettingsToFirestore(settings, actorUid)
  await setDoc(ownerSettingsRef(), {
    ...patch,
    updatedAt: serverTimestamp(),
    maintenanceMode: {
      ...patch.maintenanceMode,
      updatedAt: serverTimestamp(),
    },
  }, { merge: true })
}

export const runOwnerAction = async (input: OwnerActionInput) =>
  (await legacyCallable<OwnerActionInput, OwnerActionResult>('runOwnerAction')(input)).data

export type PlanLimitsDiffItem = {
  tier: string
  section: 'tierLimits' | 'workspaceCaps' | 'workspaceDefaults'
  key: string
  before: number
  after: number
}

export type PlanLimitsPreview = {
  diffs: PlanLimitsDiffItem[]
  changedTiers: string[]
  tierWorkspaceCounts: Partial<Record<string, number | null>>
  countErrors: string[]
  note: string
  previewToken: string
}

export const previewPlanLimits = async (input: {
  planLimits: unknown
  reason: string
}): Promise<PlanLimitsPreview> => {
  const result = await callable<OwnerActionInput, OwnerActionResult>(
    'runOwnerAction',
    {
      kind: 'SET_PLAN_LIMITS',
      dryRun: true,
      reason: input.reason,
      planLimits: input.planLimits,
    },
    { retries: 0 }
  )
  const data = (result.data || {}) as Record<string, unknown>
  return {
    diffs: (data.diffs as PlanLimitsDiffItem[]) || [],
    changedTiers: (data.changedTiers as string[]) || [],
    tierWorkspaceCounts: (data.tierWorkspaceCounts as Partial<Record<string, number | null>>) || {},
    countErrors: (data.countErrors as string[]) || [],
    note: typeof data.note === 'string' ? data.note : '',
    previewToken: typeof data.previewToken === 'string' ? data.previewToken : '',
  }
}

export const applyPlanLimits = async (input: {
  planLimits: unknown
  reason: string
  previewToken: string
}): Promise<OwnerActionResult> => {
  return callable<OwnerActionInput, OwnerActionResult>(
    'runOwnerAction',
    {
      kind: 'SET_PLAN_LIMITS',
      dryRun: false,
      reason: input.reason,
      previewToken: input.previewToken,
      planLimits: input.planLimits,
    },
    { retries: 0 }
  )
}

export const listOwnerActionLog = async () =>
  (await legacyCallable<Record<string, never>, OwnerActionLogList>('listOwnerActionLog')({})).data

export const listOwnerActivity = async (input: OwnerActivityListInput) =>
  callable<OwnerActivityListInput, OwnerActivityListResult>('listOwnerActivity', input)

export const exportOwnerActivity = async (filters: OwnerActivityExportInput) =>
  callable<OwnerActivityExportInput, OwnerActivityExportResult>('exportOwnerActivity', filters, { timeoutMs: 120_000, retries: 0 })

export const getOwnerActivityDetail = async (actionId: string) =>
  callable<{ actionId: string }, OwnerActivityDetailResult>('getOwnerActivityDetail', { actionId })

export const revertOwnerAction = async (input: { actionId: string; reason?: string; dryRun?: boolean }) =>
  callable<typeof input, OwnerRevertResult>('revertOwnerAction', input, { retries: 0 })

export type CohortRuleField =
  | 'plan'
  | 'signupAgeDays'
  | 'lastActiveDays'
  | 'openTicketCount'
  | 'workspacesOwnedCount'
  | 'personalCredits'
  | 'hasRestrictions'

export type CohortRuleOp = 'eq' | 'in' | 'lte' | 'gte'

export interface CohortRule {
  field: CohortRuleField
  op: CohortRuleOp
  value: unknown
}

export interface CohortListItem {
  id: string
  name: string
  description: string
  kind: 'user'
  match: 'all' | 'any'
  rules: CohortRule[]
  builtIn: boolean
  archived: boolean
  version: number
  memberCount: number
  manualCount: number
  capped: boolean
}

export interface CohortDetailResult extends CohortListItem {
  includeEmails?: string[]
}

export interface ListOwnerCohortsInput {
  includeArchived?: boolean
}

export interface ListOwnerCohortsResult {
  cohorts: CohortListItem[]
  capped: boolean
}

export interface GetOwnerCohortInput {
  id: string
}

export interface GetOwnerCohortResult {
  cohort: CohortDetailResult
}

export interface EstimateOwnerCohortInput {
  id?: string
  name?: string
  description?: string
  match: 'all' | 'any'
  rules: CohortRule[]
  includeEmails?: string[]
}

export interface EstimateOwnerCohortResult {
  memberCount: number
  count: number
  capped: boolean
}

export interface UpsertOwnerCohortInput {
  id?: string
  name: string
  description?: string
  match: 'all' | 'any'
  rules: CohortRule[]
  includeEmails?: string[]
  reason: string
}

export interface UpsertOwnerCohortResult {
  cohort: {
    id: string
    name: string
    description: string
    kind: 'user'
    match: 'all' | 'any'
    rules: CohortRule[]
    builtIn: boolean
    archived: boolean
    version: number
  }
}

export interface ArchiveOwnerCohortInput {
  id: string
  reason: string
}

export interface ArchiveOwnerCohortResult {
  success: boolean
  id: string
  archived: boolean
}

export interface PreviewOwnerCohortMembersInput {
  id: string
  limit?: number
}

export interface PreviewOwnerCohortMembersResult {
  members: Array<{ email: string; plan: string }>
}

export const listOwnerCohorts = async (input?: ListOwnerCohortsInput) => {
  const payload: Record<string, unknown> = {}
  if (input?.includeArchived !== undefined) {
    payload.includeArchived = input.includeArchived
  }
  return callable<typeof payload, ListOwnerCohortsResult>('listOwnerCohorts', payload)
}

export const getOwnerCohort = async (input: GetOwnerCohortInput) =>
  callable<GetOwnerCohortInput, GetOwnerCohortResult>('getOwnerCohort', { id: input.id })

export const estimateOwnerCohort = async (input: EstimateOwnerCohortInput) => {
  const payload: Record<string, unknown> = {
    match: input.match,
    rules: input.rules,
  }
  if (input.id !== undefined) payload.id = input.id
  if (input.name !== undefined) payload.name = input.name
  if (input.description !== undefined) payload.description = input.description
  if (input.includeEmails !== undefined) payload.includeEmails = input.includeEmails
  return callable<typeof payload, EstimateOwnerCohortResult>('estimateOwnerCohort', payload)
}

export const upsertOwnerCohort = async (input: UpsertOwnerCohortInput) => {
  const payload: Record<string, unknown> = {
    name: input.name,
    match: input.match,
    rules: input.rules,
    reason: input.reason,
  }
  if (input.id !== undefined) payload.id = input.id
  if (input.description !== undefined) payload.description = input.description
  if (input.includeEmails !== undefined) payload.includeEmails = input.includeEmails
  return callable<typeof payload, UpsertOwnerCohortResult>('upsertOwnerCohort', payload, { retries: 0 })
}

export const archiveOwnerCohort = async (input: ArchiveOwnerCohortInput) =>
  callable<ArchiveOwnerCohortInput, ArchiveOwnerCohortResult>('archiveOwnerCohort', { id: input.id, reason: input.reason }, { retries: 0 })

export const previewOwnerCohortMembers = async (input: PreviewOwnerCohortMembersInput) => {
  const payload: Record<string, unknown> = { id: input.id }
  if (input.limit !== undefined) payload.limit = input.limit
  return callable<typeof payload, PreviewOwnerCohortMembersResult>('previewOwnerCohortMembers', payload)
}

export interface OwnerFlagRule {
  cohortId: string
  value: boolean
  percentage: number
}

export interface OwnerFlagDetail {
  key: string
  description: string
  enforced: boolean
  defaultValue: boolean
  rules: OwnerFlagRule[]
  version: number
  updatedAt: unknown | null
  dependsOn: string[]
  killed: boolean
  killedAt: string | null
  killedBy: string | null
  killReason: string | null
}

export type FlagHistoryAction = 'update' | 'kill' | 'restore' | 'rollback'

export interface OwnerFlagHistoryEntry {
  version: number
  action: FlagHistoryAction
  defaultValue: boolean
  rules: OwnerFlagRule[]
  dependsOn: string[]
  killed: boolean
  savedAt: string | null
  savedBy: string | null
  reason: string | null
  rolledBackFrom: number | null
}

export interface FlagImpactResult {
  scanned: number
  capped: boolean
  target: {
    onBefore: number
    onAfter: number
    turnsOn: number
    turnsOff: number
  }
  dependents: Array<{
    key: string
    turnsOn: number
    turnsOff: number
  }>
}

export interface ListOwnerFlagsResult {
  flags: OwnerFlagDetail[]
}

export interface GetOwnerFlagInput {
  key: string
}

export interface GetOwnerFlagResult {
  flag: OwnerFlagDetail
}

export interface UpdateOwnerFlagInput {
  key: string
  description?: string
  defaultValue: boolean
  rules: OwnerFlagRule[]
  dependsOn: string[]
  reason: string
}

export interface UpdateOwnerFlagResult {
  flag: OwnerFlagDetail
}

export interface KillOwnerFlagInput {
  key: string
  confirmKey: string
  reason: string
}

export interface RestoreOwnerFlagInput {
  key: string
  confirmKey: string
  reason: string
}

export interface RollbackOwnerFlagInput {
  key: string
  version: number
  reason: string
}

export interface ListOwnerFlagHistoryInput {
  key: string
  beforeVersion?: number
  limit?: number
}

export interface ListOwnerFlagHistoryResult {
  entries: OwnerFlagHistoryEntry[]
  nextBeforeVersion: number | null
}

export interface EstimateOwnerFlagImpactInput {
  key: string
  change: 'update' | 'kill' | 'restore' | 'rollback'
  defaultValue?: boolean
  rules?: OwnerFlagRule[]
  dependsOn?: string[]
  version?: number
}

export const listOwnerFlags = async () =>
  callable<Record<string, never>, ListOwnerFlagsResult>('listOwnerFlags', {})

export const getOwnerFlag = async (input: GetOwnerFlagInput) =>
  callable<GetOwnerFlagInput, GetOwnerFlagResult>('getOwnerFlag', { key: input.key })

export const updateOwnerFlag = async (input: UpdateOwnerFlagInput) => {
  const payload: Record<string, unknown> = {
    key: input.key,
    defaultValue: input.defaultValue,
    rules: input.rules,
    dependsOn: input.dependsOn,
    reason: input.reason,
  }
  if (input.description !== undefined) {
    payload.description = input.description
  }
  return callable<typeof payload, UpdateOwnerFlagResult>('updateOwnerFlag', payload, { retries: 0 })
}

export const killOwnerFlag = async (input: KillOwnerFlagInput) =>
  callable<KillOwnerFlagInput, { flag: OwnerFlagDetail }>('killOwnerFlag', input, { retries: 0 })

export const restoreOwnerFlag = async (input: RestoreOwnerFlagInput) =>
  callable<RestoreOwnerFlagInput, { flag: OwnerFlagDetail }>('restoreOwnerFlag', input, { retries: 0 })

export const rollbackOwnerFlag = async (input: RollbackOwnerFlagInput) =>
  callable<RollbackOwnerFlagInput, { flag: OwnerFlagDetail }>('rollbackOwnerFlag', input, { retries: 0 })

export const listOwnerFlagHistory = async (input: ListOwnerFlagHistoryInput) =>
  callable<ListOwnerFlagHistoryInput, ListOwnerFlagHistoryResult>('listOwnerFlagHistory', input)

export const estimateOwnerFlagImpact = async (input: EstimateOwnerFlagImpactInput) =>
  callable<EstimateOwnerFlagImpactInput, FlagImpactResult>('estimateOwnerFlagImpact', input)

export interface ListOwnerFlagAdoptionInput {
  days?: number
  key?: string
}

export interface OwnerFlagAdoptionStat {
  on: number
  pct: number
}

export interface OwnerFlagAdoptionDaySummary {
  date: string
  scanned: number
  capped: boolean
  flags: Record<string, OwnerFlagAdoptionStat>
}

export interface ListOwnerFlagAdoptionResult {
  days: OwnerFlagAdoptionDaySummary[]
}

export const listOwnerFlagAdoption = async (input: ListOwnerFlagAdoptionInput) =>
  callable<ListOwnerFlagAdoptionInput, ListOwnerFlagAdoptionResult>('listOwnerFlagAdoption', input)

export const updateOwnerFeedbackStatus = async (input: { id: string; status: string; ownerNote?: string }) =>
  (await legacyCallable<typeof input, { ok: true }>('updateOwnerFeedbackStatus')(input)).data

/**
 * @deprecated Use updateSupportTicket from src/services/supportTicketsService.ts instead.
 */
export const updateOwnerDebugStatus = async (input: { id: string; status: DebugTicketStatus; ownerNote?: string; resolutionNote?: string }) =>
  (await legacyCallable<typeof input, { ok: true }>('updateOwnerDebugStatus')(input)).data

export const updateOwnerDebugReporterRestriction = async (input: { userId: string; blockNewTickets: boolean; blockReopenTickets: boolean; reason?: string }) =>
  (await legacyCallable<typeof input, { ok: true }>('updateOwnerDebugReporterRestriction')(input)).data

export const submitDebugReportToOwner = async (input: OwnerDebugReportInput) =>
  (await legacyCallable<OwnerDebugReportInput, { ok: true; severity: 'Low' | 'Medium' | 'High' }>('submitDebugReportToOwner')(input)).data

export const getDebugReportBundleUrl = async (input: { id: string; accessReason?: unknown }) =>
  (await legacyCallable<typeof input, OwnerDebugBundleUrlResult>('getDebugReportBundleUrl')(input)).data

export type BreakGlassReason =
  | 'SUPPORT_TICKET'
  | 'INCIDENT'
  | 'ABUSE_REVIEW'
  | 'USER_REQUEST'
  | 'LEGAL_REQUEST'
  | 'DATA_MIGRATION'

export interface BeginBreakGlassInput {
  reason: BreakGlassReason
  linkedId: string
  elaboration?: string
  subject: string
  targetWorkspaceId?: string
  revealDurationSec?: number
  notifyOwner?: boolean
  sessionId?: string
}

export interface BreakGlassGrantResult {
  ok: boolean
  grantId: string
  revealToken: string
  expiresAt: number
  revealDurationSec: number
  subject: string
}

export interface BreakGlassStatusResult {
  active: boolean
  grantId?: string
  subject?: string
  reason?: BreakGlassReason
  linkedId?: string
  expiresAt?: number
  remainingSec?: number
  totalDurationSec?: number
}

export const beginContentBreakGlass = async (input: BeginBreakGlassInput) =>
  (await legacyCallable<BeginBreakGlassInput, BreakGlassGrantResult>('beginContentBreakGlass')({
    sessionId: getOwnerTabSessionId(),
    ...input,
  })).data

export const extendContentBreakGlass = async (input: { grantId: string; extraDurationSec?: number; sessionId?: string }) =>
  (await legacyCallable<typeof input, { ok: boolean; grantId: string; revealToken: string; expiresAt: number; totalDurationSec: number }>('extendContentBreakGlass')({
    sessionId: getOwnerTabSessionId(),
    ...input,
  })).data

export const endContentBreakGlass = async (input: { grantId: string; sessionId?: string }) =>
  (await legacyCallable<{ grantId: string; sessionId?: string }, { ok: boolean; revoked: boolean }>('endContentBreakGlass')({
    sessionId: getOwnerTabSessionId(),
    ...input,
  })).data

export const getBreakGlassStatus = async (input: { grantId: string; revealToken?: string; subject?: string; sessionId?: string }) =>
  (await legacyCallable<typeof input, BreakGlassStatusResult>('getBreakGlassStatus')({
    sessionId: getOwnerTabSessionId(),
    ...input,
  })).data

export const readBundleRaw = async (input: { ticketId: string; grantId: string; revealToken: string; sessionId?: string }) =>
  (await legacyCallable<typeof input, OwnerDebugBundleUrlResult>('readBundleRaw')({
    sessionId: getOwnerTabSessionId(),
    ...input,
  })).data

export interface RepairOpsPlaceReadAccessInput {
  workspaceId?: string
  dryRun?: boolean
}

export interface RepairPlaceAccessRow {
  workspaceId: string
  name: string
  scanned: number
  missing: number
  updated: number
}

export const repairOpsPlaceReadAccess = async (
  input?: RepairOpsPlaceReadAccessInput,
): Promise<RepairPlaceAccessRow[]> => {
  return callable<RepairOpsPlaceReadAccessInput, RepairPlaceAccessRow[]>('repairOpsPlaceReadAccess', input ?? {})
}

export interface SupportAccessLogEntry {
  id: string
  actorUid: string
  actorEmail?: string
  actorRole: string
  callable: string
  pathClass: string
  workspaceId?: string | null
  subjectUid?: string | null
  targetId?: string | null
  itemCount?: number | null
  reason?: { code: string; linkedId?: string }
  breakGlassGrantId?: string | null
  environment?: string
  createdAt?: unknown
  createdAtMillis?: number
}

export interface ListSupportAccessLogFilters {
  workspaceId?: string
  actorUid?: string
  pathClass?: string
  fromMillis?: number
  toMillis?: number
}

export interface ListSupportAccessLogResult {
  items: SupportAccessLogEntry[]
  nextCursor: { id: string } | null
  hasMore: boolean
}

export const listSupportAccessLog = async (
  filters?: ListSupportAccessLogFilters,
  cursor?: { id: string } | null,
): Promise<ListSupportAccessLogResult> => {
  return callable<{ filters?: ListSupportAccessLogFilters; cursor?: { id: string } | null }, ListSupportAccessLogResult>(
    'listSupportAccessLog',
    { filters, cursor },
  )
}

export interface SetHosPatientLegalSetupInput {
  workspaceId: string
  confirmed: boolean
  note?: string
}

export interface SetHosPatientLegalSetupResult {
  success: boolean
  legalSetupConfirmed: boolean
}

export const setHosPatientLegalSetup = async (
  input: SetHosPatientLegalSetupInput,
): Promise<SetHosPatientLegalSetupResult> => {
  return callable<SetHosPatientLegalSetupInput, SetHosPatientLegalSetupResult>(
    'setHosPatientLegalSetup',
    input,
  )
}


